import React, { useMemo, useState } from 'react';
import './StorefrontManager.css';
import StorefrontNav from './StorefrontNav';
import { useStorefrontSettings } from './useStorefrontSettings';
import AddressSection from './sections/AddressSection';
import BasicsSection from './sections/BasicsSection';
import ImagerySection from './sections/ImagerySection';
import LocationSection from './sections/LocationSection';
import InternationalizationSection from './sections/InternationalizationSection';
import BrandingSection from './sections/BrandingSection';
import CustomizationSection from './sections/CustomizationSection';
import SocialLinksSection from './sections/SocialLinksSection';
import LinktreeSection from './sections/LinktreeSection';
import FriendsSection from './sections/FriendsSection';
import TextCopySection from './sections/TextCopySection';
import CustomCssSection from './sections/CustomCssSection';
import VacationSection from './sections/VacationSection';
import StorefrontPreview from './StorefrontPreview';

const SECTION_DEFS = [
    { key: 'address', label: 'Website URL', Component: AddressSection },
    { key: 'basics', label: 'Basics', Component: BasicsSection },
    { key: 'imagery', label: 'Imagery', Component: ImagerySection },
    { key: 'location', label: 'Location', Component: LocationSection },
    { key: 'branding', label: 'Colors', Component: BrandingSection },
    { key: 'customization', label: 'Customization', Component: CustomizationSection },
    {
        key: 'social-links',
        label: 'Social Links',
        Component: SocialLinksSection,
        componentProps: { wrapperElement: 'div' },
    },
    {
        key: 'linktree',
        label: 'Linktree',
        Component: LinktreeSection,
        componentProps: { wrapperElement: 'div' },
    },
    {
        key: 'friends',
        label: 'Friends',
        Component: FriendsSection,
        componentProps: { wrapperElement: 'div' },
    },
    { key: 'textcopy', label: 'Text Copy', Component: TextCopySection },
    {
        key: 'internationalization',
        label: 'Internationalization',
        Component: InternationalizationSection,
    },
    { key: 'customcss', label: 'Custom CSS', Component: CustomCssSection },
    { key: 'vacation', label: 'Vacation', Component: VacationSection },
];

function getInitialSection() {
    const requestedSection = new URLSearchParams(window.location.search).get('section');
    return SECTION_DEFS.some(section => section.key === requestedSection)
        ? requestedSection
        : SECTION_DEFS[0].key;
}

export default function StorefrontManager() {
    const settings = useStorefrontSettings();
    const [activeSection, setActiveSection] = useState(getInitialSection);
    const [savingSection, setSavingSection] = useState(null);
    const [previewVersion, setPreviewVersion] = useState(0);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [showPreviewAfterSave, setShowPreviewAfterSave] = useState(false);
    const [showPreviewSavedMessage, setShowPreviewSavedMessage] = useState(false);

    const activeDef = useMemo(
        () => SECTION_DEFS.find(section => section.key === activeSection) || SECTION_DEFS[0],
        [activeSection]
    );
    const ActiveComponent = activeDef.Component;

    async function handleSave(sectionKey, fn) {
        setSavingSection(sectionKey);
        try {
            const result = await fn();
            if (result?.success) {
                setPreviewVersion(version => version + 1);
                setShowPreviewSavedMessage(true);
                if (showPreviewAfterSave) setIsPreviewOpen(true);
            }
            return result;
        } finally {
            setSavingSection(null);
        }
    }

    function handleStorefrontChanged() {
        setPreviewVersion(version => version + 1);
        setShowPreviewSavedMessage(true);
        if (showPreviewAfterSave) setIsPreviewOpen(true);
    }

    if (settings.loading) {
        return <section className="sfm-loading" aria-busy="true" aria-label="Loading storefront settings"><p className="help">Loading storefront settings...</p></section>;
    }

    if (settings.loadError) {
        return (
            <section className="sfm-load-error" role="alert">
                <div className="error">{settings.loadError}</div>
                <button type="button" className="button" onClick={settings.reload}>Try Again</button>
            </section>
        );
    }

    return (
        <section className="sfm-manager" aria-label="Storefront Settings">
            <div className="sfm-manager-header">
                <div>
                    <h1>Storefront Settings</h1>
                    <p className="help">Manage your saved public storefront.</p>
                </div>
                <button
                    type="button"
                    className="button secondary"
                    onClick={() => {
                        setShowPreviewSavedMessage(false);
                        setIsPreviewOpen(true);
                    }}
                    disabled={!settings.data?.url}
                >
                    Preview Storefront
                </button>
            </div>
            <div className="sfm-layout">
                <StorefrontNav
                    sections={SECTION_DEFS}
                    activeSection={activeSection}
                    onSelect={setActiveSection}
                />
                <div className="sfm-panel">
                    <ActiveComponent
                        {...activeDef.componentProps}
                        data={settings.data}
                        actions={settings}
                        saving={savingSection === activeDef.key}
                        onSave={(fn) => handleSave(activeDef.key, fn)}
                        onStorefrontChanged={handleStorefrontChanged}
                    />
                </div>
            </div>
            <div className="sfm-manager-footer">
                <label className="checkbox" htmlFor="sfm-show-preview-after-save">
                    <input
                        id="sfm-show-preview-after-save"
                        type="checkbox"
                        checked={showPreviewAfterSave}
                        onChange={event => setShowPreviewAfterSave(event.target.checked)}
                    />
                    <span>Show changes after saving?</span>
                </label>
            </div>
            <StorefrontPreview
                isOpen={isPreviewOpen}
                onClose={() => {
                    setIsPreviewOpen(false);
                    setShowPreviewSavedMessage(false);
                }}
                url={settings.data?.url}
                version={previewVersion}
                onRefresh={() => setPreviewVersion(version => version + 1)}
                showSavedMessage={showPreviewSavedMessage}
                scale={0.5}
            />
        </section>
    );
}
